import { displayFieldName } from '@eis/patterns/reporting';

/** A custom dataset document (`api/schemas/dataservice/dataset.schema.json`, SDD-024 S-1). */
export interface DatasetDocument {
  readonly schemaVersion: 1;
  readonly kind: 'dataset';
  readonly code?: string;
  readonly ern?: string;
  readonly name: string;
  readonly description?: string;
  readonly sources: readonly { readonly alias: string; readonly view: string }[];
  readonly joins?: readonly {
    readonly left: string;
    readonly right: string;
    readonly type: 'INNER' | 'LEFT';
    readonly on: readonly {
      readonly leftField: string;
      readonly rightField: string;
      readonly cast?: 'STRING' | 'NUMBER';
    }[];
  }[];
  readonly fields: readonly {
    readonly source: string;
    readonly field: string;
    readonly alias: string;
  }[];
  readonly calculatedFields?: readonly {
    readonly name: string;
    readonly expression: string;
    readonly type: string;
  }[];
  readonly filters?: { readonly join: 'ALL' | 'ANY'; readonly clauses: readonly unknown[] };
}

export const ALIASES = ['a', 'b', 'c', 'd'] as const;

export function asDatasetDocument(
  json: Record<string, unknown> | undefined,
): DatasetDocument | undefined {
  if (json?.kind !== 'dataset') return undefined;
  return json as unknown as DatasetDocument;
}

/**
 * A new custom dataset of two views joined on one field (the datasets page's "Eigener Datensatz",
 * scenario U-9): LEFT JOIN, the join field and the product name of A, the join field of B — each
 * under a unique alias. The builder edits the rest.
 */
export function newDatasetDocument(
  name: string,
  base: string,
  joined: string,
  on: string,
  baseFields: readonly string[],
): DatasetDocument {
  const fields: { source: string; field: string; alias: string }[] = [
    { source: 'a', field: on, alias: displayFieldName(on) },
  ];
  if (baseFields.includes('produkt name') && on !== 'produkt name')
    fields.push({ source: 'a', field: 'produkt name', alias: displayFieldName('produkt name') });
  fields.push({ source: 'b', field: on, alias: `${displayFieldName(on)} (B)` });
  return {
    schemaVersion: 1,
    kind: 'dataset',
    name,
    sources: [
      { alias: 'a', view: base },
      { alias: 'b', view: joined },
    ],
    joins: [{ left: 'a', right: 'b', type: 'LEFT', on: [{ leftField: on, rightField: on }] }],
    fields,
    filters: { join: 'ALL', clauses: [] },
  };
}

/** Unique aliases (S-1): a second use of an alias is the one the builder flags. */
export function duplicateAliases(doc: DatasetDocument): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const f of doc.fields) {
    if (seen.has(f.alias)) dup.add(f.alias);
    seen.add(f.alias);
  }
  return [...dup];
}
